import {
  DataDrivenAnimation,
  LeadAnimation,
  AppointmentAnimation,
  MarketingAnimation,
  WorkflowAnimation,
} from "@/components/ui/lottie-animation";
import {
  UserCheck,
  CalendarDays,
  Target,
  BarChart,
  Megaphone,
  Brain,
} from "lucide-react";
import { ComponentType } from "react";

export type UseCase = {
  slug: string;
  title: string;
  description: string;
  icon: any;
  animationComponent?: ComponentType;
  content: {
    hero: string;
    details: string;
    example: string;
    keywords: string;
  };
  howItWorksTitle: string;
  howItWorks: {
    title: string;
    description: string;
  }[];
};

export const useCases: UseCase[] = [
  {
    slug: "customer-support-automation",
    title: "Customer Support",
    description:
      "Provide 24/7 support and answer up to 80% of routine customer questions instantly.",
    icon: UserCheck,
    animationComponent: LeadAnimation,
    content: {
      hero: "Free your team from answering the same questions over and over. Our AI agents act as your first line of support, providing instant, accurate answers to common inquiries.",
      details:
        "Integrate a chatbot on your website or a voice agent on your phone lines to handle FAQs, provide business hours, explain services, and more, around the clock.",
      example:
        "A pet groomer in Austin reduced support calls by 30% by implementing a website chatbot that answered questions about pricing, services, and availability.",
      keywords:
        "AI for customer support, automated customer service, 24/7 support chatbot",
    },
    howItWorksTitle: "Smarter Customer Support, Around the Clock",
    howItWorks: [
      {
        title: "24/7 AI Chat Agents",
        description:
          "Provide instant responses to common customer queries any time of day, improving satisfaction and reducing workload.",
      },
      {
        title: "Intent Detection",
        description:
          "Understand customer intent automatically to route queries to the right department or provide accurate responses.",
      },
      {
        title: "Seamless Escalation",
        description:
          "Effortlessly escalate complex queries to human agents while retaining full context for faster resolution.",
      },
    ],
  },
  {
    slug: "appointment-scheduling",
    title: "Appointment Scheduling",
    description:
      "Automate your booking process and reduce no-shows with intelligent reminders.",
    icon: CalendarDays,
    animationComponent: AppointmentAnimation,
    content: {
      hero: "Let AI manage your calendar. Our agents can book, reschedule, and cancel appointments through voice or chat, integrating directly with your existing calendar system.",
      details:
        "The system can also send automated, personalized reminders via SMS or voice call to reduce no-shows and confirm appointments, ensuring your schedule stays full.",
      example:
        "A dental practice in Dallas cut patient no-shows by 20% and recovered dozens of hours per month in administrative time with our AI appointment scheduling and reminder system.",
      keywords:
        "AI appointment scheduling, automated booking system, reduce no-shows",
    },
    howItWorksTitle: "Never Miss an Appointment Again",
    howItWorks: [
      {
        title: "Automated Scheduling",
        description:
          "Clients book appointments online through a smart system that syncs with your calendar in real time.",
      },
      {
        title: "Personalized Reminders",
        description:
          "Send personalized reminders to clients to confirm appointments and keep them engaged.",
      },
      {
        title: "Real-Time Confirmation",
        description:
          "Clients can confirm their appointments in real time, reducing no-shows and improving your schedule.",
      },
    ],
  },
  {
    slug: "lead-generation",
    title: "Lead Generation",
    description:
      "Capture and qualify leads from your website and social media automatically.",
    icon: Target,
    animationComponent: LeadAnimation,
    content: {
      hero: "Never miss a potential customer again. Our AI chatbots proactively engage website visitors, ask qualifying questions, and collect contact information to grow your sales pipeline.",
      details:
        "The chatbot can be programmed with your specific qualifying criteria and will seamlessly hand off hot leads to your sales team or book a discovery call directly on their calendar.",
      example:
        "A home services business in Houston increased their qualified leads by 15% in the first month by using our chatbot to engage visitors and collect their project details.",
      keywords:
        "AI lead generation, chatbot for lead qualification, website lead capture",
    },
    howItWorksTitle: "Turn Visitors into Quality Leads",
    howItWorks: [
      {
        title: "AI Chat Funnels",
        description:
          "Engage visitors with intelligent chatbots that ask qualifying questions and capture key contact details.",
      },
      {
        title: "Smart Lead Scoring",
        description:
          "Automatically score leads based on behavior, engagement, and demographics to prioritize follow-ups.",
      },
      {
        title: "CRM Integration",
        description:
          "Instantly sync captured leads into your CRM or sales pipeline for immediate nurturing.",
      },
    ],
  },
  {
    slug: "marketing-automation",
    title: "Marketing Automation",
    description:
      "Automate personalized email campaigns, social media posts, and customer follow-ups.",
    icon: Megaphone,
    animationComponent: MarketingAnimation,
    content: {
      hero: "Put your marketing on autopilot. We build AI-driven workflows that nurture leads, engage existing customers, and promote your services across multiple channels.",
      details:
        "From sending personalized follow-up emails after a service to scheduling a month's worth of social media content, our automation solutions save you time and deliver better marketing results.",
      example:
        "A fitness studio boosted membership renewals by 25% by implementing an automated email and SMS campaign that re-engages members before their subscription expires.",
      keywords:
        "AI marketing automation, automated email campaigns, social media automation",
    },
    howItWorksTitle: "Automate, Personalize, and Scale Your Marketing",
    howItWorks: [
      {
        title: "Personalized Campaigns",
        description:
          "Create dynamic email and ad campaigns tailored to customer preferences and behavior.",
      },
      {
        title: "Smart Scheduling",
        description:
          "Send communications at the right time based on user activity and engagement patterns.",
      },
      {
        title: "Cross-Channel Automation",
        description:
          "Automatically manage marketing across email, social media, and SMS in a single flow.",
      },
    ],
  },
  {
    slug: "data-driven-insights",
    title: "Data-Driven Insights",
    description:
      "Turn your business data into actionable insights with AI-powered analysis.",
    icon: BarChart,
    animationComponent: DataDrivenAnimation,
    content: {
      hero: "Make smarter business decisions. Our RAG systems can analyze your internal documents, customer feedback, and sales data to provide accurate answers and uncover hidden trends.",
      details:
        'Ask natural language questions about your business (e.g., "What was our most popular service last month?") and get instant, data-backed answers without needing to sift through spreadsheets or reports.',
      example:
        "A chiropractic clinic uses a RAG system to quickly analyze patient feedback forms, identifying common concerns and areas for service improvement.",
      keywords:
        "data-driven insights for small business, AI business analysis, RAG for data analysis",
    },
    howItWorksTitle: "Insights That Power Smarter Decisions",
    howItWorks: [
      {
        title: "AI-Powered Dashboards",
        description:
          "Visualize key metrics and trends from your business data in real time using intelligent dashboards.",
      },
      {
        title: "Predictive Analytics",
        description:
          "Forecast future outcomes based on historical data and customer behavior.",
      },
      {
        title: "Automated Reports",
        description:
          "Generate insightful reports automatically and deliver them to stakeholders on a schedule.",
      },
    ],
  },
  {
    slug: "workflow-optimization",
    title: "Workflow Optimization",
    description:
      "Automate your business processes and improve efficiency with AI-driven workflows.",
    icon: Brain,
    animationComponent: WorkflowAnimation ,
    content: {
      hero: "Streamline your business operations with AI-driven workflows. Our agents can automate repetitive tasks, manage inventory, and handle customer inquiries, freeing up your team to focus on what really matters.",
      details:
        "The system can also send automated, personalized reminders via SMS or voice call to reduce no-shows and confirm appointments, ensuring your schedule stays full.",
      example:
        "A dental practice in Dallas cut patient no-shows by 20% and recovered dozens of hours per month in administrative time with our AI appointment scheduling and reminder system.",
      keywords:
        "AI workflow optimization, automated business processes, inventory management",
    },
    howItWorksTitle: "Streamline Every Step of Your Operations",
    howItWorks: [
      {
        title: "Task Automation",
        description:
          "Automate repetitive tasks like data entry, notifications, and form processing to save time.",
      },
      {
        title: "Process Intelligence",
        description:
          "Analyze workflows to identify inefficiencies and optimize processes using AI recommendations.",
      },
      {
        title: "Custom Triggers & Actions",
        description:
          "Create smart automation rules that trigger actions based on business events or data changes.",
      },
    ],
  },
];
